import { useMemo, useState, useEffect, useRef } from 'react';
import { useUser as useClerkUser, useClerk } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';

export type UserRole = 'owner' | 'seeker' | 'vendor' | null;

export type UnifiedUser = {
  id: string;
  email: string;
  name: string;
  fullName?: string;
  primaryEmailAddress?: { emailAddress: string };
  imageUrl?: string;
  unsafeMetadata?: Record<string, any>;
  isAdmin?: boolean;
  role?: UserRole;
  provider: 'clerk';
  createdAt?: Date | string | null;
  update?: (params: { unsafeMetadata: Record<string, any> }) => Promise<any>;
};

export function useAuth() {
  const { user: clerkUser, isLoaded: clerkLoaded, isSignedIn: clerkSignedIn } = useClerkUser();
  const { signOut } = useClerk();
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  // Reactive role state strictly scoped per user ID (Finding P2)
  const [role, setRole] = useState<UserRole>(() => {
    if (typeof window !== 'undefined' && clerkUser?.id) {
      return (sessionStorage.getItem(`espacero_user_role_${clerkUser.id}`) as UserRole) || null;
    }
    return null;
  });

  // Central identity switch / sign-out detector:
  // Whenever Clerk user ID changes (including signout via UserButton), flush query cache immediately.
  useEffect(() => {
    const currentId = clerkUser?.id || null;
    if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== currentId) {
      qc.clear();
    }
    prevUserIdRef.current = currentId;

    if (currentId && typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(`espacero_user_role_${currentId}`) as UserRole;
      setRole(stored || null);
    } else {
      setRole(null);
    }
  }, [clerkUser?.id, qc]);

  // Reactive role updates via window event
  useEffect(() => {
    const onRoleChanged = (e: Event) => {
      const customEvent = e as CustomEvent<{ userId: string; role: UserRole }>;
      if (customEvent.detail?.userId === clerkUser?.id) {
        setRole(customEvent.detail.role);
      }
    };
    window.addEventListener('espacero_role_changed', onRoleChanged);
    return () => window.removeEventListener('espacero_role_changed', onRoleChanged);
  }, [clerkUser?.id]);

  // Create a unified user object - MEMOIZED to prevent infinite loops in useEffect dependencies
  const user = useMemo<UnifiedUser | null>(() => {
    if (clerkSignedIn && clerkUser) {
      return {
        id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || '',
        name: clerkUser.fullName || clerkUser.firstName || 'User',
        fullName: clerkUser.fullName || '',
        primaryEmailAddress: { emailAddress: clerkUser.primaryEmailAddress?.emailAddress || '' },
        imageUrl: clerkUser.imageUrl,
        unsafeMetadata: clerkUser.unsafeMetadata || {},
        isAdmin: clerkUser.publicMetadata?.role === 'admin',
        role: role,
        provider: 'clerk',
        createdAt: clerkUser.createdAt,
        update: (params: any) => clerkUser.update(params)
      };
    }
    return null;
  }, [clerkUser, clerkSignedIn, role]);

  return {
    user,
    isSignedIn: !!user,
    isLoaded: clerkLoaded,
    provider: user?.provider || null,
    getToken: async () => {
      if (clerkSignedIn && clerkUser) {
        try {
          return await (window as any).Clerk?.session?.getToken();
        } catch (e) {
          console.error('[useAuth] Failed to get Clerk token', e);
          return null;
        }
      }
      return null;
    },
    logout: async () => {
      if (typeof window !== 'undefined') {
        if (clerkUser?.id) {
          sessionStorage.removeItem(`user_synced_v2_${clerkUser.id}`);
          sessionStorage.removeItem(`espacero_user_role_${clerkUser.id}`);
        }
      }
      qc.clear();
      await signOut();
    }
  };
}

